/**
 * The HR (people-ops) slice of the wire, and the paths the HR screens call.
 *
 * ── Why this file exists next to `types.ts`, `paths.ts` and `recruiting.ts` ──
 *
 * `types.ts`'s own header sets the rule the whole client follows: transcribe the slice
 * a screen actually reads, not the 10 000-line contract, so there is never a second
 * full copy to keep in step. The HR module (Phase 3a) is a new area — employee
 * documents and contracts (Task 12), and onboarding templates/checklists/tasks (Task
 * 13) — separate enough from both the workforce screens and the recruiting ones that
 * it earns its own file, exactly as `recruiting.ts` did. Later HR tasks (leave,
 * reviews) bring the slice they need here.
 *
 * These types are **hand-written to mirror `openapi.yaml`** — the `HrEmployeeDocument`,
 * `HrContract`, `OnboardingTemplate`, `OnboardingTemplateItem`, `OnboardingTask`,
 * `OnboardingChecklist` and their request/response schemas. They are not generated from
 * it and this task does not touch `openapi.yaml`; a field that is `nullable` on the wire
 * is `T | null` here, and a field the contract marks optional is optional here, because
 * `exactOptionalPropertyTypes` is on and an absent value and a `null` one are different
 * facts (`PRODUCT.md` §4).
 *
 * ── The employee id is a text input, not a directory pick ────────────────────
 *
 * Every HR resource here is keyed by an `employeeId` path param (or, for the checklist
 * list, an `employee_id` query). `GET /employees` (Task T2-A) and `EmployeePicker`
 * (Task T2-B) exist now, but these screens have not been rewired to use them, so they
 * still take the id from a text field — the same pattern the recruiting screens use
 * for their recruiter/hiring-manager id inputs. The server resolves the WHOSE gate
 * over the caller's tree, so an id outside their field of view answers `403`/`404`; the
 * client asserts nothing by naming one.
 *
 * Nothing here authorises anything. The reads gate on `hr.view_employee_hr` (and the
 * download on the stronger `hr.view_employee_documents`); the writes gate on
 * `hr.manage_employee_documents` / `hr.manage_onboarding`. Each is a **hybrid** of that
 * KIND code and a `workforce.view_*` WHOSE code the server resolves on every call,
 * whatever the menu renders.
 */

import { qs, type QueryValue } from './paths';

/* ───────────────────────────────── employee HR documents (Task 12, WP-W6) ── */

/**
 * `HrEmployeeDocument.doc_type` — the kind of official document. `null` is a value on
 * the wire (no kind recorded); the register request has no `null` because omission is
 * how "unset" is said there — except the register request makes `doc_type` **required**,
 * so it is never absent going out.
 */
export const HR_DOCUMENT_TYPES = [
  'id_card',
  'passport',
  'contract',
  'certificate',
  'tax',
  'other',
] as const;

export type HrDocumentType = (typeof HR_DOCUMENT_TYPES)[number];

/**
 * `HrEmployeeDocument` of `openapi.yaml` — an employee HR document's **metadata**, never
 * its bytes and never a download URL. `required: [id, employee_id, filename,
 * uploaded_at]`; every other member is nullable on the wire and so `T | null` here — the
 * server sends the key with a `null` value, it does not omit it.
 */
export interface HrEmployeeDocument {
  readonly id: string;
  readonly employee_id: string;
  readonly doc_type: HrDocumentType | null;
  readonly filename: string;
  readonly content_type: string | null;
  readonly file_size_bytes: number | null;
  readonly checksum_sha256: string | null;
  readonly effective_from: string | null;
  readonly effective_to: string | null;
  readonly uploaded_by: string | null;
  readonly uploaded_at: string;
  readonly expires_at: string | null;
}

/**
 * `HrEmployeeDocumentRegisterRequest`. `filename` and `doc_type` are required;
 * `content_type` and `file_size_bytes` are present-or-absent and are filled from the
 * selected `File` (`file.type`, `file.size`) when the browser knows them. The two dates
 * are optional and omitted when blank.
 */
export interface HrEmployeeDocumentRegisterRequest {
  readonly filename: string;
  readonly doc_type: HrDocumentType;
  readonly content_type?: string;
  readonly file_size_bytes?: number;
  readonly effective_from?: string;
  readonly effective_to?: string;
}

/**
 * `HrEmployeeDocumentUpload` — the freshly registered document, and a short-lived
 * **presigned PUT URL** to upload its bytes to object storage directly. The URL is a
 * bearer credential (`openapi.yaml`: "Not logged; treat as a credential"): it is spent
 * immediately by a single `PUT` and never stored.
 */
export interface HrEmployeeDocumentUpload {
  readonly document: HrEmployeeDocument;
  readonly upload_url: string;
  readonly expires_at: string;
}

/**
 * `HrEmployeeDocumentListResponse` — a bare `{ data: [...] }`, newest first, with **no**
 * `page` and no cursor, so the documents of one employee are read whole.
 */
export interface HrEmployeeDocumentListResponse {
  readonly data: readonly HrEmployeeDocument[];
}

/* ─────────────────────────────────────── employment contracts (Task 12) ── */

/** `HrContract.contract_type` — `null` is a value on the wire (no type recorded). */
export const HR_CONTRACT_TYPES = ['permanent', 'fixed_term', 'probation', 'intern'] as const;

export type HrContractType = (typeof HR_CONTRACT_TYPES)[number];

/** `HrContract.status` — the contract lifecycle. `null` is a value on the wire. */
export const HR_CONTRACT_STATUSES = ['draft', 'active', 'expired', 'terminated'] as const;

export type HrContractStatus = (typeof HR_CONTRACT_STATUSES)[number];

/**
 * `HrContract` of `openapi.yaml`. `required: [id, employee_id, start_date, created_at,
 * updated_at]`; every other member is nullable on the wire and so `T | null` here.
 * `base_salary` is an **exact decimal string** (`NUMERIC(14,2)`), never a JS number, so
 * no cent is lost to a float — even though the create request takes a number.
 */
export interface HrContract {
  readonly id: string;
  readonly employee_id: string;
  readonly contract_type: HrContractType | null;
  readonly start_date: string;
  readonly end_date: string | null;
  readonly base_salary: string | null;
  readonly currency: string | null;
  readonly status: HrContractStatus | null;
  readonly document_s3_key: string | null;
  /** The recruiting application this hire grew out of, if any. */
  readonly source_application_id: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * `HrContractCreateRequest`. `contract_type` and `start_date` are required; every other
 * field is present-or-absent and omitted when blank. `base_salary` is a **number** on
 * the request (the wire's `type: number`), even though it comes back as a decimal string
 * — a difference the contract has and this client mirrors rather than papers over.
 */
export interface HrContractCreateRequest {
  readonly contract_type: HrContractType;
  readonly start_date: string;
  readonly end_date?: string;
  readonly base_salary?: number;
  readonly currency?: string;
  readonly status?: HrContractStatus;
  readonly document_s3_key?: string;
  readonly source_application_id?: string;
}

/** `HrContractListResponse` — a bare `{ data: [...] }`, most recent first, no page. */
export interface HrContractListResponse {
  readonly data: readonly HrContract[];
}

/* ─────────────────────────── onboarding templates + items (Task 13, WP-W6) ── */

/** `OnboardingTemplateItem.category` / `OnboardingTask.category` — `null` is a value. */
export const ONBOARDING_CATEGORIES = ['it', 'hr', 'manager'] as const;

export type OnboardingCategory = (typeof ONBOARDING_CATEGORIES)[number];

/**
 * `OnboardingTemplate` of `openapi.yaml` — a company's standard onboarding process.
 * Company configuration, not data about any one employee. `required: [id, name,
 * is_active, created_at, updated_at]`; `created_by` is nullable on the wire.
 */
export interface OnboardingTemplate {
  readonly id: string;
  readonly name: string;
  readonly is_active: boolean;
  readonly created_by: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * `OnboardingTemplateItem` of `openapi.yaml` — one step of a template. `required: [id,
 * template_id, title, sort_order, default_offset_days, created_at, updated_at]`;
 * `category` is nullable on the wire. `default_offset_days` is how many days after a
 * checklist's start the instantiated task falls due.
 */
export interface OnboardingTemplateItem {
  readonly id: string;
  readonly template_id: string;
  readonly title: string;
  readonly category: OnboardingCategory | null;
  readonly sort_order: number;
  readonly default_offset_days: number;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * `OnboardingTemplateCreateRequest`. Only `name` is required; `is_active` is optional
 * (the server defaults it) and omitted when the form leaves it at its default.
 */
export interface OnboardingTemplateCreateRequest {
  readonly name: string;
  readonly is_active?: boolean;
}

/**
 * `OnboardingTemplateItemCreateRequest`. Only `title` is required; `category`,
 * `sort_order` and `default_offset_days` are present-or-absent and omitted when blank.
 */
export interface OnboardingTemplateItemCreateRequest {
  readonly title: string;
  readonly category?: OnboardingCategory;
  readonly sort_order?: number;
  readonly default_offset_days?: number;
}

/** `OnboardingTemplateListResponse` — a bare `{ data: [...] }`, newest first, no page. */
export interface OnboardingTemplateListResponse {
  readonly data: readonly OnboardingTemplate[];
}

/** `OnboardingTemplateItemListResponse` — the items of one template, in sort order. */
export interface OnboardingTemplateItemListResponse {
  readonly data: readonly OnboardingTemplateItem[];
}

/* ─────────────────────────── onboarding checklists + tasks (Task 13) ── */

/** `OnboardingTask.status` — the task lifecycle. */
export const ONBOARDING_TASK_STATUSES = ['pending', 'done', 'skipped', 'blocked'] as const;

export type OnboardingTaskStatus = (typeof ONBOARDING_TASK_STATUSES)[number];

/** `OnboardingChecklist.status` — the checklist lifecycle. */
export const ONBOARDING_CHECKLIST_STATUSES = ['in_progress', 'completed', 'cancelled'] as const;

export type OnboardingChecklistStatus = (typeof ONBOARDING_CHECKLIST_STATUSES)[number];

/**
 * `OnboardingTask` of `openapi.yaml` — one task of a checklist, nested inside it.
 * `required: [id, checklist_id, title, status, created_at, updated_at]`; every other
 * member is nullable on the wire and so `T | null` here.
 */
export interface OnboardingTask {
  readonly id: string;
  readonly checklist_id: string;
  readonly title: string;
  readonly category: OnboardingCategory | null;
  readonly assignee_employee_id: string | null;
  readonly due_date: string | null;
  readonly status: OnboardingTaskStatus;
  readonly completed_by: string | null;
  readonly completed_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * `OnboardingChecklist` of `openapi.yaml` — one employee's onboarding checklist, with
 * its tasks nested. `required: [id, employee_id, status, created_at, updated_at, tasks]`;
 * `template_id`, `source_application_id`, `started_at` and `completed_at` are nullable on
 * the wire.
 */
export interface OnboardingChecklist {
  readonly id: string;
  readonly employee_id: string;
  readonly template_id: string | null;
  readonly source_application_id: string | null;
  readonly status: OnboardingChecklistStatus;
  readonly started_at: string | null;
  readonly completed_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly tasks: readonly OnboardingTask[];
}

/**
 * `OnboardingChecklistCreateRequest`. Only `employee_id` is required; a `template_id`,
 * when given, instantiates one task per template item. `source_application_id` is
 * present-or-absent, omitted when blank.
 */
export interface OnboardingChecklistCreateRequest {
  readonly employee_id: string;
  readonly template_id?: string;
  readonly source_application_id?: string;
}

/** `OnboardingChecklistListResponse` — one employee's checklists, newest first, no page. */
export interface OnboardingChecklistListResponse {
  readonly data: readonly OnboardingChecklist[];
}

/* ────────────────────────────────── leave / time-off (Task 14, WP-W7) ────── */

/**
 * `LeaveRequestRecord.status` of `openapi.yaml`. No "cancelled by someone else" — a
 * `decided_by` on a `cancelled` row says who withdrew or cancelled it.
 */
export const LEAVE_REQUEST_STATUSES = ['pending', 'approved', 'rejected', 'cancelled'] as const;

export type LeaveRequestStatus = (typeof LEAVE_REQUEST_STATUSES)[number];

/**
 * `LeaveType` of `openapi.yaml` — a company's leave catalogue entry (annual, sick,
 * unpaid). Company configuration, not data about any one employee; `required: [id,
 * name, code, is_paid, default_annual_days, is_active, created_at, updated_at]`, with
 * `code` and `default_annual_days` nullable on the wire.
 */
export interface LeaveType {
  readonly id: string;
  readonly name: string;
  readonly code: string | null;
  readonly is_paid: boolean;
  readonly default_annual_days: number | null;
  readonly is_active: boolean;
  readonly created_at: string;
  readonly updated_at: string;
}

/** `LeaveTypeListResponse` — a bare `{ data: [...] }`, active first then by name. */
export interface LeaveTypeListResponse {
  readonly data: readonly LeaveType[];
}

/**
 * `LeaveBalance` of `openapi.yaml` — one employee's balance for a type in a year.
 * `remaining_days` is **computed** (`accrued_days − used_days`) and never stored; every
 * member of `required` is present, so nothing here is nullable.
 */
export interface LeaveBalance {
  readonly id: string;
  readonly employee_id: string;
  readonly leave_type_id: string;
  readonly year: number;
  readonly accrued_days: number;
  readonly used_days: number;
  readonly remaining_days: number;
  readonly created_at: string;
  readonly updated_at: string;
}

/** `LeaveBalanceListResponse` — one employee's balances, newest year first, no page. */
export interface LeaveBalanceListResponse {
  readonly data: readonly LeaveBalance[];
}

/**
 * `LeaveRequestRecord` of `openapi.yaml` — a leave request with its approval workflow.
 * `days` is the count of working days (Mon–Fri) in the range, computed server-side and
 * never trusted from a client. `required: [id, employee_id, leave_type_id, start_date,
 * end_date, days, reason, status, decided_by, decided_at, created_at, updated_at]`, so
 * `reason`, `decided_by` and `decided_at` are `T | null` here rather than optional — the
 * server sends the key with a `null` value.
 */
export interface LeaveRequestRecord {
  readonly id: string;
  readonly employee_id: string;
  readonly leave_type_id: string;
  readonly start_date: string;
  readonly end_date: string;
  readonly days: number;
  readonly reason: string | null;
  readonly status: LeaveRequestStatus;
  readonly decided_by: string | null;
  readonly decided_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/** `LeaveRequestListResponse` — one employee's requests, newest range first, no page. */
export interface LeaveRequestListResponse {
  readonly data: readonly LeaveRequestRecord[];
}

/**
 * `LeaveRequestCreateRequest` of `openapi.yaml`. `leave_type_id`, `start_date` and
 * `end_date` are required; `reason` is present-or-absent, omitted when blank.
 *
 * ── There is no `employee_id` field, and that is the whole point ────────────
 *
 * `openapi.yaml`'s own description: "The `employee_id` is deliberately absent — a leave
 * request's subject is the session, never the body. An employee requests only their own
 * leave." `POST /hr/leave/requests` reads `actor.employeeId` off the session
 * (`leave-service.ts`'s `createLeaveRequest`) and there is no path by which a client
 * could name a different subject even if it tried — the schema this interface mirrors
 * has no member for one.
 */
export interface LeaveRequestCreateRequest {
  readonly leave_type_id: string;
  readonly start_date: string;
  readonly end_date: string;
  readonly reason?: string;
}

/* ─────────────────────────── performance — cycles, goals, reviews (Task 15, WP-W8) ──
 *
 * `openapi.yaml`'s `/hr/performance/*` surface. Company-config half (review cycles) and
 * per-employee half (goals, reviews) are kept apart the same way onboarding's templates
 * and checklists are, because they authorise differently:
 *
 *  - A **review cycle** ("H2 2026") is company configuration, gated on an `hr.*` code
 *    at company scope, no employee-tree — like `OnboardingTemplate`.
 *  - A **goal** and a **review** are about one employee, gated on a **hybrid**: an
 *    `hr.*` KIND code (`hr.view_reviews`/`hr.view_own_review` to read, `hr.submit_review`
 *    or `hr.manage_review_cycles` to write) **and** the `workforce.view_*` WHOSE code the
 *    server resolves over the caller's tree.
 *
 * ── `ReviewSubmitRequest` has no reviewer field, and that is the whole point ──────
 *
 * `openapi.yaml`'s own description: "`reviewer_employee_id` is deliberately absent — a
 * review's reviewer is the session, never the body." `resolveReviewSubject`
 * (`src/modules/hr/performance-service.ts`) forces the reviewer to `actor.employeeId`
 * on every submit; nothing a client sends can name a different one, because there is no
 * member here for it to occupy. `employee_id` is the review's SUBJECT, not its reviewer,
 * and it is optional for exactly one reason: for `kind: 'self'` it is omitted (or, if
 * sent, must equal the session) and the subject becomes the caller; for `kind: 'manager'`
 * or `'peer'` it is required and must name someone else. The self-review form built on
 * this type sends no `employee_id` at all — the cleanest way to say "myself" is to say
 * nothing.
 */

/** `ReviewCycle.status` / `ReviewCycleCreateRequest.status` — the cycle lifecycle. */
export const REVIEW_CYCLE_STATUSES = ['draft', 'open', 'closed'] as const;

export type ReviewCycleStatus = (typeof REVIEW_CYCLE_STATUSES)[number];

/** `Goal.status` / `GoalCreateRequest.status` — a goal's outcome. */
export const GOAL_STATUSES = ['active', 'achieved', 'missed', 'cancelled'] as const;

export type GoalStatus = (typeof GOAL_STATUSES)[number];

/** `Review.kind` / `ReviewSubmitRequest.kind` — who the review is by, relative to its subject. */
export const REVIEW_KINDS = ['self', 'manager', 'peer'] as const;

export type ReviewKind = (typeof REVIEW_KINDS)[number];

/**
 * `ReviewCycle` of `openapi.yaml` — a company's review calendar entry. `required: [id,
 * name, period_start, period_end, status, created_by, created_at, updated_at]`;
 * `period_start`, `period_end` and `created_by` are nullable on the wire.
 */
export interface ReviewCycle {
  readonly id: string;
  readonly name: string;
  readonly period_start: string | null;
  readonly period_end: string | null;
  readonly status: ReviewCycleStatus;
  readonly created_by: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/** `ReviewCycleCreateRequest`. Only `name` is required; the rest is present-or-absent. */
export interface ReviewCycleCreateRequest {
  readonly name: string;
  readonly period_start?: string;
  readonly period_end?: string;
  readonly status?: ReviewCycleStatus;
}

/** `ReviewCycleListResponse` — a bare `{ data: [...] }`, newest period first, no page. */
export interface ReviewCycleListResponse {
  readonly data: readonly ReviewCycle[];
}

/**
 * `Goal` of `openapi.yaml` — one employee's goal, optionally attached to a cycle.
 * `required: [id, employee_id, cycle_id, title, description, measure, status, weight,
 * created_by, created_at, updated_at]`; `cycle_id`, `description`, `measure`, `weight`
 * and `created_by` are nullable on the wire.
 */
export interface Goal {
  readonly id: string;
  readonly employee_id: string;
  readonly cycle_id: string | null;
  readonly title: string;
  readonly description: string | null;
  readonly measure: string | null;
  readonly status: GoalStatus;
  readonly weight: number | null;
  readonly created_by: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * `GoalCreateRequest`. `employee_id` and `title` are required — a goal is assigned by
 * someone else, never self-created, so `employee_id` names the subject rather than
 * being resolved from the session. Every other field is present-or-absent, omitted when
 * blank.
 */
export interface GoalCreateRequest {
  readonly employee_id: string;
  readonly title: string;
  readonly cycle_id?: string;
  readonly description?: string;
  readonly measure?: string;
  readonly status?: GoalStatus;
  readonly weight?: number;
}

/** `GoalListResponse` — one employee's goals, newest first, no page. */
export interface GoalListResponse {
  readonly data: readonly Goal[];
}

/**
 * `Review` of `openapi.yaml` — a self/manager/peer review of an employee for a cycle.
 * `required: [id, cycle_id, employee_id, reviewer_employee_id, kind, overall, narrative,
 * productivity_score_id, submitted_at, created_at, updated_at]`; `overall`, `narrative`,
 * `productivity_score_id` and `submitted_at` are nullable on the wire.
 * `reviewer_employee_id` is always the session that submitted it — never a value a
 * request set.
 */
export interface Review {
  readonly id: string;
  readonly cycle_id: string;
  readonly employee_id: string;
  readonly reviewer_employee_id: string;
  readonly kind: ReviewKind;
  readonly overall: number | null;
  readonly narrative: string | null;
  readonly productivity_score_id: string | null;
  readonly submitted_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * `ReviewSubmitRequest`. `cycle_id` and `kind` are required. `employee_id` is
 * **optional** — see this section's header comment — and every other member is
 * present-or-absent, omitted when blank. There is no `reviewer_employee_id` member:
 * the reviewer is always the session (`api/hr.ts`'s own header on this type).
 */
export interface ReviewSubmitRequest {
  readonly cycle_id: string;
  readonly kind: ReviewKind;
  readonly employee_id?: string;
  readonly overall?: number;
  readonly narrative?: string;
  readonly productivity_score_id?: string;
}

/** `ReviewListResponse` — an employee's reviews for a cycle, newest first, no page. */
export interface ReviewListResponse {
  readonly data: readonly Review[];
}

/* ─────────────────────────────────────────────────── path builders (WP-W6) ── */

export const HR_EMPLOYEES_PATH = '/api/v1/hr/employees';
export const HR_ONBOARDING_TEMPLATES_PATH = '/api/v1/hr/onboarding/templates';
export const HR_ONBOARDING_CHECKLISTS_PATH = '/api/v1/hr/onboarding/checklists';

/**
 * `GET`/`POST /hr/employees/{employeeId}/documents` — the HR document metadata of one
 * employee (`GET`), and the register-and-get-a-presigned-PUT endpoint (`POST`). An
 * employee absent from the caller's company answers `404`.
 */
export function hrEmployeeDocumentsPath(employeeId: string): string {
  return `${HR_EMPLOYEES_PATH}/${encodeURIComponent(employeeId)}/documents`;
}

/**
 * `GET /hr/employees/{employeeId}/documents/{docId}/download` — the one sensitive,
 * access-logged read of an employee's HR file. It requires the stronger
 * `hr.view_employee_documents` and a mandatory `X-Access-Reason`, and `302`s to a
 * short-lived presigned GET URL that the browser follows.
 */
export function hrEmployeeDocumentDownloadPath(employeeId: string, docId: string): string {
  return `${hrEmployeeDocumentsPath(employeeId)}/${encodeURIComponent(docId)}/download`;
}

/**
 * `GET`/`POST /hr/employees/{employeeId}/contracts` — the employment contracts of one
 * employee (`GET`, most recent first), and the create of a new one (`POST`). An employee
 * absent from the caller's company answers `404`.
 */
export function hrEmployeeContractsPath(employeeId: string): string {
  return `${HR_EMPLOYEES_PATH}/${encodeURIComponent(employeeId)}/contracts`;
}

/** `GET`/`POST /hr/onboarding/templates/{id}/items` — the steps of one template. */
export function onboardingTemplateItemsPath(templateId: string): string {
  return `${HR_ONBOARDING_TEMPLATES_PATH}/${encodeURIComponent(templateId)}/items`;
}

/**
 * `GET /hr/onboarding/checklists?employee_id=…` — the onboarding checklists of one
 * employee, newest first, each with its tasks nested. `employee_id` is **required** by
 * the contract, so this builder takes it rather than accepting an empty query.
 */
export function onboardingChecklistsPath(query: { readonly employee_id: string }): string {
  const params: Readonly<Record<string, QueryValue>> = { employee_id: query.employee_id };
  return `${HR_ONBOARDING_CHECKLISTS_PATH}${qs(params)}`;
}

/**
 * `POST /hr/onboarding/checklists/{id}/tasks/{taskId}/complete` — mark one task done.
 * Carries no request body; returns the checklist with its (now updated) tasks.
 */
export function onboardingTaskCompletePath(checklistId: string, taskId: string): string {
  return `${HR_ONBOARDING_CHECKLISTS_PATH}/${encodeURIComponent(
    checklistId,
  )}/tasks/${encodeURIComponent(taskId)}/complete`;
}

/* ─────────────────────────────────────────────── leave path builders (WP-W7) ── */

export const HR_LEAVE_TYPES_PATH = '/api/v1/hr/leave/types';
export const HR_LEAVE_BALANCES_PATH = '/api/v1/hr/leave/balances';
export const HR_LEAVE_REQUESTS_PATH = '/api/v1/hr/leave/requests';

/**
 * `GET /hr/leave/balances?employee_id=…` — one employee's leave balances.
 *
 * `employee_id` is **required** by the contract (unlike the self-views `paths.ts`'s own
 * header describes), because this one read serves two callers: an employee reading their
 * own balances, who passes their own `SessionUser.employee_id`, and a manager or HR
 * admin reading an employee in their tree, who names that employee. Naming an id never
 * widens scope — the hybrid gate in `leave-authorizer.ts` still resolves WHOSE over the
 * caller's field of view on every call.
 */
export function hrLeaveBalancesPath(employeeId: string): string {
  return `${HR_LEAVE_BALANCES_PATH}${qs({ employee_id: employeeId })}`;
}

/**
 * `GET /hr/leave/requests?employee_id=…&status=…` — one employee's leave requests,
 * newest range first, optionally narrowed to one `status` — a **server** filter, not a
 * client-side one over a page already in hand.
 */
export function hrLeaveRequestsPath(query: {
  readonly employeeId: string;
  readonly status?: LeaveRequestStatus | undefined;
}): string {
  return `${HR_LEAVE_REQUESTS_PATH}${qs({
    employee_id: query.employeeId,
    status: query.status,
  })}`;
}

/** `POST /hr/leave/requests/{id}/approve` — approve a pending request. */
export function hrLeaveRequestApprovePath(requestId: string): string {
  return `${HR_LEAVE_REQUESTS_PATH}/${encodeURIComponent(requestId)}/approve`;
}

/** `POST /hr/leave/requests/{id}/reject` — reject a pending request. */
export function hrLeaveRequestRejectPath(requestId: string): string {
  return `${HR_LEAVE_REQUESTS_PATH}/${encodeURIComponent(requestId)}/reject`;
}

/** `POST /hr/leave/requests/{id}/cancel` — the requester withdraws, or an approver cancels. */
export function hrLeaveRequestCancelPath(requestId: string): string {
  return `${HR_LEAVE_REQUESTS_PATH}/${encodeURIComponent(requestId)}/cancel`;
}

/* ─────────────────────────────────────────── performance path builders (WP-W8) ── */

export const HR_PERFORMANCE_REVIEW_CYCLES_PATH = '/api/v1/hr/performance/review-cycles';
export const HR_PERFORMANCE_GOALS_PATH = '/api/v1/hr/performance/goals';
export const HR_PERFORMANCE_REVIEWS_PATH = '/api/v1/hr/performance/reviews';
export const HR_PERFORMANCE_REVIEWS_MINE_PATH = '/api/v1/hr/performance/reviews/mine';

/**
 * `GET /hr/performance/goals?employee_id=…&cycle_id=…` — one employee's goals,
 * optionally narrowed to one cycle. `employee_id` is **required** by the contract and
 * serves both callers exactly as `hrLeaveBalancesPath` does: an employee naming their
 * own id (My review), and a manager/HR admin naming one of theirs.
 */
export function hrPerformanceGoalsPath(query: {
  readonly employeeId: string;
  readonly cycleId?: string | undefined;
}): string {
  return `${HR_PERFORMANCE_GOALS_PATH}${qs({
    employee_id: query.employeeId,
    cycle_id: query.cycleId,
  })}`;
}

/**
 * `GET /hr/performance/reviews?employee_id=…&cycle_id=…` — one employee's reviews (both
 * self- and manager/peer-authored), optionally narrowed to one cycle. This is the
 * team/HR-admin read over `hr.view_reviews`; an employee reading their own without that
 * code uses `HR_PERFORMANCE_REVIEWS_MINE_PATH` instead, which takes no `employee_id` at
 * all — the subject is forced to the session server-side.
 */
export function hrPerformanceReviewsPath(query: {
  readonly employeeId: string;
  readonly cycleId?: string | undefined;
}): string {
  return `${HR_PERFORMANCE_REVIEWS_PATH}${qs({
    employee_id: query.employeeId,
    cycle_id: query.cycleId,
  })}`;
}
